/**
 * Data Protection Policy Modal Component
 * Displays data protection policy information in a modal overlay
 */

import { i18n } from '../utils/i18n'

export class DataProtectionModal {
  private modal: HTMLElement | null = null

  constructor() {
    this.createModal()
    this.attachEventListeners()
  }

  /**
   * Create the modal element
   */
  private createModal(): void {
    const modal = document.createElement('div')
    modal.id = 'data-protection-modal'
    modal.className = 'privacy-modal'
    modal.style.display = 'none'

    modal.innerHTML = `
      <div class="privacy-modal-overlay"></div>
      <div class="privacy-modal-content">
        <button class="privacy-modal-close" aria-label="Cerrar">&times;</button>
        <h2 class="privacy-modal-title" data-i18n="dataProtection.title">Política de Protección de Datos</h2>
        <div class="privacy-modal-body">
          <div class="privacy-section">
            <p data-i18n="dataProtection.intro">
              APRIORI LEGAL SAS es una sociedad con domicilio principal en la ciudad de Medellín, Colombia, comprometida con el cumplimiento de las normas en materia de datos personales. En virtud de lo anterior, APRIORI LEGAL SAS se compromete a proteger la privacidad por el uso, recopilación y tratamiento de los datos personales de clientes, proveedores y empleados. APRIORI LEGAL SAS guarda estricta confidencialidad sobre los datos personales y garantiza el derecho que tienen los TITULARES de conocer, actualizar y rectificar la información contenida en nuestras bases de datos.
            </p>
          </div>

          <div class="privacy-section">
            <h3 data-i18n="dataProtection.treatment.title">Tratamiento de la Información</h3>
            <p data-i18n="dataProtection.treatment.content">
              Los datos personales incorporados por APRIORI LEGAL SAS en las bases de datos y archivos, son tratados exclusivamente para las finalidades establecidas. Cada dato personal utilizado por APRIORI LEGAL SAS cuenta con la respectiva autorización. Una vez culminada la relación contractual, comercial o de cualquier otra naturaleza entre el Titular del dato personal y la Compañía, cesará el tratamiento de los datos personales, siempre que el Titular no tenga el deber legal o contractual de permanecer en la base de datos.
            </p>
          </div>

          <div class="privacy-section">
            <h3 data-i18n="dataProtection.duties.title">Deberes de APRIORI LEGAL SAS como Responsable del Tratamiento</h3>
            <ul>
              <li data-i18n="dataProtection.duties.item1">Solicitar y conservar, en las condiciones previstas en la ley, copia de la respectiva autorización otorgada por el Titular.</li>
              <li data-i18n="dataProtection.duties.item2">Informar debidamente al Titular sobre la finalidad de la recolección y los derechos que le asisten por virtud de la autorización otorgada.</li>
              <li data-i18n="dataProtection.duties.item3">Conservar la información bajo las condiciones de seguridad necesarias para impedir su adulteración, pérdida, consulta, uso o acceso no autorizado o fraudulento.</li>
              <li data-i18n="dataProtection.duties.item4">Garantizar que la información que se suministre al Encargado del Tratamiento sea veraz, completa, exacta, actualizada, comprobable y comprensible.</li>
              <li data-i18n="dataProtection.duties.item5">Tramitar las consultas y reclamos formulados en los términos señalados en la ley.</li>
            </ul>
          </div>

          <div class="privacy-section">
            <h3 data-i18n="dataProtection.rights.title">Derechos y Procedimientos</h3>
            <p data-i18n="dataProtection.rights.intro">
              APRIORI LEGAL SAS garantizará a los TITULARES de la información, el acceso, la rectificación y eliminación de los datos personales que hayan sido objeto de un tratamiento y a solicitar prueba de la autorización otorgada para su tratamiento.
            </p>
            <h4 data-i18n="dataProtection.rights.access.title">Derecho de Acceso</h4>
            <p data-i18n="dataProtection.rights.access.content">
              Toda persona tendrá derecho a conocer si sus datos personales han sido sometidos a un tratamiento por parte de APRIORI LEGAL SAS en los términos expresados en la Ley de Protección de Datos.
            </p>
            <h4 data-i18n="dataProtection.rights.rectification.title">Derechos de Rectificación y Cancelación</h4>
            <p data-i18n="dataProtection.rights.rectification.content">
              Los TITULARES de la información tienen el derecho de comprobar frente al responsable del tratamiento de la información, la exactitud y veracidad del dato y solicitar su rectificación cuando los mismos resulten inexactos, estén incompletos o conlleven a error.
            </p>
          </div>

          <div class="privacy-section">
            <h3 data-i18n="dataProtection.procedures.title">Procedimientos de Consulta y Reclamos</h3>
            <p data-i18n="dataProtection.procedures.content">
              Para ejercer estos derechos, el titular de la información podrá hacerlo a través de los medios habilitados por APRIORI LEGAL SAS:
            </p>
            <p><strong data-i18n="dataProtection.procedures.phone">Teléfono:</strong> 3124453206</p>
            <p><strong data-i18n="dataProtection.procedures.email">Correo electrónico:</strong> administracion@apriorilegal.co</p>
            <p><strong data-i18n="dataProtection.procedures.address">Dirección:</strong> Calle 4 Sur # 43A-195, El Poblado, Medellín</p>
          </div>

          <div class="privacy-section">
            <h3 data-i18n="dataProtection.validity.title">Vigencia de la Política</h3>
            <p data-i18n="dataProtection.validity.content">
              Las bases de datos tendrán una vigencia igual al periodo en que se mantenga la finalidad o finalidades del tratamiento en cada base de datos, o el periodo de vigencia señalado en la Ley, los contratos, o formatos de captura respectiva.
            </p>
          </div>

          <div class="privacy-section privacy-doc-link">
            <a href="/politica-tratamiento-datos-personales.pdf" target="_blank" rel="noopener noreferrer" data-i18n="dataProtection.pdfLink">Descargar la Política de Tratamiento de Datos Personales (PDF)</a>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(modal)
    this.modal = modal
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    if (!this.modal) return

    // Close button
    const closeBtn = this.modal.querySelector('.privacy-modal-close')
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close())
    }

    // Overlay click
    const overlay = this.modal.querySelector('.privacy-modal-overlay')
    if (overlay) {
      overlay.addEventListener('click', () => this.close())
    }

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.style.display === 'flex') {
        this.close()
      }
    })

    // Listen for language changes
    window.addEventListener('languageChanged', () => {
      this.updateContent()
    })
  }

  /**
   * Update modal content with current language
   */
  private updateContent(): void {
    if (!this.modal) return

    const elements = this.modal.querySelectorAll('[data-i18n]')
    elements.forEach((element) => {
      const key = element.getAttribute('data-i18n')
      if (key) {
        const translation = i18n.t(key)
        element.textContent = translation
      }
    })
  }

  /**
   * Open the modal
   */
  open(): void {
    if (this.modal) {
      this.modal.style.display = 'flex'
      document.body.style.overflow = 'hidden'
      this.updateContent()
    }
  }

  /**
   * Close the modal
   */
  close(): void {
    if (this.modal) {
      this.modal.style.display = 'none'
      document.body.style.overflow = 'auto'
    }
  }
}

// Create and export singleton instance
export const dataProtectionModal = new DataProtectionModal()
